import { Head, useForm, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  NavLink,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import {
  Globe,
  GitBranch,
  Mail,
  Monitor,
  Package,
  Plus,
} from 'lucide-react'
import type { FormEvent, ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'
import AppLayout from '@/components/AppLayout'
import CenteredEmptyState from '@/components/CenteredEmptyState'
import SidebarLayout from '@/components/SidebarLayout'
import type { ProjectServicesPageProps, ServiceProps } from '@/types'

const TYPE_ICONS: Record<string, typeof Globe> = {
  domain: Globe,
  webspace: Monitor,
  email: Mail,
  git_repo: GitBranch,
}

const TYPE_LABELS: Record<string, string> = {
  domain: 'Domains',
  webspace: 'Webspaces',
  email: 'Email',
  git_repo: 'Git Repos',
}

const TYPE_LABEL_SINGULAR: Record<string, string> = {
  domain: 'Domain',
  webspace: 'Webspace',
  email: 'Email',
  git_repo: 'Git Repo',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  provisioning: 'yellow',
  suspended: 'red',
  deleted: 'gray',
}

const CONFIG_FIELDS: Record<string, Array<{ key: string; label: string; placeholder?: string; required?: boolean }>> = {
  domain: [
    { key: 'domain_name', label: 'Domain name', placeholder: 'example.com', required: true },
    { key: 'registrar', label: 'Registrar', placeholder: 'e.g. Cloudflare' },
  ],
  webspace: [
    { key: 'webspace_id', label: 'Webspace ID', placeholder: 'e.g. ws-123', required: true },
    { key: 'runtime', label: 'Runtime', placeholder: 'e.g. ruby, node, static' },
    { key: 'domain', label: 'Domain', placeholder: 'e.g. example.com' },
  ],
  email: [
    { key: 'email_address', label: 'Email address', placeholder: 'user@example.com', required: true },
    { key: 'imap_host', label: 'IMAP host', placeholder: 'imap.example.com' },
    { key: 'smtp_host', label: 'SMTP host', placeholder: 'smtp.example.com' },
  ],
  git_repo: [
    { key: 'repo_url', label: 'Repository URL', placeholder: 'https://github.com/org/repo', required: true },
    { key: 'default_branch', label: 'Default branch', placeholder: 'main' },
    { key: 'hosting', label: 'Hosting provider', placeholder: 'e.g. github, gitlab' },
  ],
}

function ServiceForm({
  projectId,
  service,
  serviceTypes,
  providers,
  statuses,
  onCancel,
}: {
  projectId: string
  service?: ServiceProps
  serviceTypes: string[]
  providers: string[]
  statuses: string[]
  onCancel: () => void
}) {
  const { csrf_token } = usePage().props

  const isEdit = !!service

  const { data, setData, post, patch, processing, reset } = useForm({
    service: {
      name: service?.name ?? '',
      service_type: service?.service_type ?? serviceTypes[0] ?? '',
      provider: service?.provider ?? 'platform',
      status: service?.status ?? 'active',
      config: (service?.config ?? {}) as Record<string, string>,
    },
    authenticity_token: csrf_token as string,
  })

  const configFields = CONFIG_FIELDS[data.service.service_type] ?? []

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      patch(`/projects/${projectId}/services/${service.id}`, {
        preserveScroll: true,
        onSuccess: () => onCancel(),
      })
    } else {
      post(`/projects/${projectId}/services`, {
        preserveScroll: true,
        onSuccess: () => {
          reset()
          onCancel()
        },
      })
    }
  }

  const setConfig = (key: string, value: string) => {
    setData('service', {
      ...data.service,
      config: { ...data.service.config, [key]: value },
    })
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg" withBorder>
      <Stack gap="md">
        <Title order={3}>{isEdit ? 'Edit service' : 'New service'}</Title>

        <TextInput
          label="Name"
          value={data.service.name}
          onChange={(e) => setData('service', { ...data.service, name: e.target.value })}
          required
        />

        <Select
          label="Type"
          data={serviceTypes.map((t) => ({ value: t, label: TYPE_LABEL_SINGULAR[t] ?? t }))}
          value={data.service.service_type}
          onChange={(v) => {
            if (v) setData('service', { ...data.service, service_type: v, config: {} })
          }}
          disabled={isEdit}
        />

        <Select
          label="Provider"
          data={providers.map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
          value={data.service.provider}
          onChange={(v) => {
            if (v) setData('service', { ...data.service, provider: v })
          }}
        />

        {isEdit && (
          <Select
            label="Status"
            data={statuses.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={data.service.status}
            onChange={(v) => {
              if (v) setData('service', { ...data.service, status: v })
            }}
          />
        )}

        {configFields.length > 0 && (
          <>
            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mt="xs">Configuration</Text>
            {configFields.map((field) => (
              <TextInput
                key={field.key}
                label={field.label}
                placeholder={field.placeholder}
                value={(data.service.config[field.key] as string) ?? ''}
                onChange={(e) => setConfig(field.key, e.target.value)}
                required={field.required}
              />
            ))}
          </>
        )}

        <Group>
          <Button type="submit" loading={processing}>{isEdit ? 'Save' : 'Create'}</Button>
          <Button type="button" variant="default" onClick={() => { reset(); onCancel() }}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function ServiceDetail({
  service,
  onEdit,
}: {
  service: ServiceProps
  onEdit: () => void
}) {
  const Icon = TYPE_ICONS[service.service_type] ?? Package
  const configFields = CONFIG_FIELDS[service.service_type] ?? []

  return (
    <Paper p="xl" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <Icon size={24} strokeWidth={1.5} />
            <div>
              <Title order={3}>{service.name}</Title>
              <Text size="sm" c="dimmed">
                {TYPE_LABEL_SINGULAR[service.service_type] ?? service.service_type}
                {' '}
                &middot; {service.provider}
              </Text>
            </div>
          </Group>

          <Group gap="xs">
            <Badge color={STATUS_COLORS[service.status] ?? 'gray'} variant="light">
              {service.status}
            </Badge>
            <Button variant="default" size="sm" onClick={onEdit}>
              Edit
            </Button>
          </Group>
        </Group>

        {configFields.length > 0 && (
          <Box>
            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb="xs">Configuration</Text>
            <Stack gap="xs">
              {configFields.map((field) => {
                const value = service.config[field.key]
                if (value === undefined || value === null || value === '') return null
                return (
                  <Group key={field.key} gap="xs">
                    <Text size="sm" c="dimmed" w={140}>{field.label}:</Text>
                    <Text size="sm">{String(value)}</Text>
                  </Group>
                )
              })}
            </Stack>
          </Box>
        )}

        <Text size="xs" c="dimmed">
          Created {new Date(service.created_at).toLocaleDateString()}
          {' '}&middot;{' '}
          Updated {new Date(service.updated_at).toLocaleDateString()}
        </Text>
      </Stack>
    </Paper>
  )
}

function ProjectServicesIndex() {
  const { project, services, service_types, providers, statuses } =
    usePage<ProjectServicesPageProps>().props

  // Check URL for pre-selected service
  const urlParams = new URLSearchParams(window.location.search)
  const initialSelectedId = urlParams.get('selected')

  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(false)

  const selectedService = useMemo(
    () => services.find((s) => s.id === selectedId) ?? null,
    [services, selectedId],
  )

  const grouped = useMemo(() => {
    const groups: Record<string, ServiceProps[]> = {}
    for (const type of service_types) {
      const items = services.filter((s) => s.service_type === type)
      if (items.length > 0) groups[type] = items
    }
    return groups
  }, [services, service_types])

  const selectService = useCallback((id: string) => {
    setSelectedId(id)
    setCreating(false)
    setEditing(false)
  }, [])

  const startCreating = useCallback(() => {
    setCreating(true)
    setEditing(false)
    setSelectedId(null)
  }, [])

  return (
    <>
      <Head title={`Resources - ${project.name}`} />

      <SidebarLayout sidebar={({ closeSidebar }) => (
        <>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group justify="space-between" align="center">
              <Text fw={600} size="sm" lineClamp={1}>{project.name}</Text>
              <ActionIcon variant="light" size="sm" onClick={startCreating} title="New service">
                <Plus size={14} />
              </ActionIcon>
            </Group>
          </div>

          <ScrollArea style={{ flex: 1 }}>
            <Stack gap={0} p="xs">
              {Object.keys(grouped).length === 0 ? (
                <Text size="sm" c="dimmed" p="sm">No services yet.</Text>
              ) : (
                Object.entries(grouped).map(([type, items]) => {
                  const Icon = TYPE_ICONS[type] ?? Package
                  return (
                    <NavLink
                      key={type}
                      label={TYPE_LABELS[type] ?? type}
                      leftSection={<Icon size={16} />}
                      defaultOpened
                      childrenOffset={0}
                    >
                      {items.map((service) => (
                        <NavLink
                          key={service.id}
                          label={service.name}
                          description={service.provider}
                          active={service.id === selectedId && !creating}
                          rightSection={
                            <Badge
                              size="xs"
                              color={STATUS_COLORS[service.status] ?? 'gray'}
                              variant="light"
                            >
                              {service.status}
                            </Badge>
                          }
                          onClick={() => { selectService(service.id); closeSidebar(); }}
                        />
                      ))}
                    </NavLink>
                  )
                })
              )}
            </Stack>
          </ScrollArea>
        </>
      )}>
        <ScrollArea style={{ flex: 1 }}>
          {creating ? (
            <div style={{ padding: 24 }}>
              <ServiceForm
                projectId={project.id}
                serviceTypes={service_types}
                providers={providers}
                statuses={statuses}
                onCancel={() => setCreating(false)}
              />
            </div>
          ) : editing && selectedService ? (
            <div style={{ padding: 24 }}>
              <ServiceForm
                projectId={project.id}
                service={selectedService}
                serviceTypes={service_types}
                providers={providers}
                statuses={statuses}
                onCancel={() => setEditing(false)}
              />
            </div>
          ) : selectedService ? (
            <div style={{ padding: 24 }}>
              <ServiceDetail
                service={selectedService}
                onEdit={() => setEditing(true)}
              />
            </div>
          ) : (
            <CenteredEmptyState
              icon={Package}
              title={services.length === 0 ? 'No services yet' : 'Select a service'}
              description={services.length === 0
                ? 'Create the first service using the + button in the sidebar.'
                : 'Choose a service from the sidebar to view its details.'}
            />
          )}
        </ScrollArea>
      </SidebarLayout>
    </>
  )
}

ProjectServicesIndex.layout = (page: ReactNode) => <AppLayout padding={0}>{page}</AppLayout>

export default ProjectServicesIndex
